import { useParams } from 'wouter';
import { AdminOrganizations } from '@platform/client';
import { VettingStatus } from '@platform/client/schemas';
import { Button } from '@platform/components';
import { useState, useRef } from 'react';
import cx from 'classix';
import { ReviewPageWrapper, ReviewItem } from '../../AdminViewPage';
import { VettingModal } from './VettingModal';

export const OrganizationVetting = () => {
  const { organizationId } = useParams();
  const {
    data: organization,
    isLoading,
    isError,
    isSuccess,
  } = AdminOrganizations.useGetReviewData({
    organization_id: Number(organizationId),
  });
  const [updateVettingData, setUpdateVettingData] = useState<{
    organizationId: number;
    vettingStatus: VettingStatus;
  }>();
  const topRef = useRef<HTMLDivElement>(null);

  if (!organization) {
    return null;
  }
  return (
    <ReviewPageWrapper
      title={organization.name}
      isLoading={isLoading}
      isError={isError}
      isSuccess={isSuccess}
      topRef={topRef}
    >
      <div
        className={cx(
          'max-w-1/2 text-lg text-center mx-auto rounded-md px-2 italic',
          organization.vettingStatus === VettingStatus.transform && 'bg-success-500 text-white',
          organization.vettingStatus === VettingStatus.match && 'bg-success-100 text-black',
          organization.vettingStatus === VettingStatus.pending && 'bg-warning-100',
          organization.vettingStatus === VettingStatus.rejected && 'bg-error text-white',
          organization.vettingStatus === VettingStatus.info_requested && 'bg-warning-25',
          organization.vettingStatus === VettingStatus.signup_incomplete &&
            'bg-gray-200 text-white',
        )}
      >
        <h2>Current Status: {organization.vettingStatus}</h2>
      </div>
      <ReviewItem label='Location'>{organization.country}</ReviewItem>
      <ReviewItem label='Type'>{organization.type}</ReviewItem>
      <ReviewItem label='Size'>{organization.size}</ReviewItem>
      <ReviewItem label='Registered'>{organization.registrationDate}</ReviewItem>
      <ReviewItem label='Partners'>
        <div className='inline'>
          {organization.parents.map((parent, index) => (
            <span key={`${index}-${parent}`}>
              {parent}
              {index < organization.parents.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      </ReviewItem>
      <ReviewItem label='Industries'>
        <div className='inline'>
          {organization.industries.map((industry, index) => (
            <span key={`${index}-${industry}`}>
              {industry}
              {index < organization.industries.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      </ReviewItem>
      <ReviewItem label='Causes'>
        <div className='inline'>
          {organization.causes.map((cause, index) => (
            <span key={`${index}-${cause}`}>
              {cause}
              {index < organization.causes.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      </ReviewItem>
      <ReviewItem label='Website'>
        {organization.website && (
          <a
            className='text-brand-500 hover:underline'
            href={
              organization.website.startsWith('http') ?
                organization.website
              : `https://${organization.website}`
            }
            target='_blank'
          >
            {organization.website}
          </a>
        )}
      </ReviewItem>
      <ReviewItem label='CRM'>
        <a
          className='hover:underline text-brand-500'
          href={`https://crm.zoho.com/crm/org667587627/search?searchword=${encodeURIComponent(organization.name)}`}
          target='_blank'
        >
          Organization Search
        </a>
      </ReviewItem>
      <ReviewItem label='Description'>
        <p dangerouslySetInnerHTML={{ __html: organization.description }} />
      </ReviewItem>
      <ReviewItem label='Impact Story'>
        <p dangerouslySetInnerHTML={{ __html: organization.impactStory }} />
      </ReviewItem>
      <div className='flex justify-between gap-2 mt-3'>
        <Button variant='secondary-color' size='md' href='/organizations'>
          Back to Organizations
        </Button>
        <div className='flex gap-4'>
          <Button
            variant='primary'
            size='md'
            onPress={() => {
              setUpdateVettingData({
                organizationId: organization.organizationId,
                vettingStatus: organization.vettingStatus,
              });
            }}
          >
            Update Vetting
          </Button>
        </div>
      </div>
      {updateVettingData && (
        <VettingModal
          onClose={() => setUpdateVettingData(undefined)}
          organizationId={updateVettingData.organizationId}
          vettingStatus={updateVettingData.vettingStatus}
        />
      )}
    </ReviewPageWrapper>
  );
};
